// src/pages/proposals/ProposalInsights.tsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ScrollToTop from "../../../core/common/ScrollToTop";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Dropdown,
  Form,
} from "react-bootstrap";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip as ReTooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { TooltipProps } from "recharts";
import { useSelector } from "react-redux";
import { RootState } from "../../../core/data/redux/store";
import {
  fetchProposalsByUserId,
  ProposalResponse,
} from "../../../api/proposal"; // your api helper
import dayjs from "dayjs";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import Tip from "../../../core/common/tooltip";

const COLORS = ["#4f46e5", "#10b981", "#f59e0b", "#ef4444", "#6366f1"];
const STATUS_COLORS: Record<string, string> = {
  Draft: "#f59e0b",
  Accepted: "#10b981",
  Declined: "#ef4444",
  // fallback color for others
};
type OwnerStatusKey = "Accepted" | "Draft" | "Declined";

type TimeframeKey =
  | "last_week"
  | "this_month"
  | "last_month"
  | "3_months"
  | "6_months"
  | "this_year"
  | "last_year"
  | "overall";

type ProductStatusKey = "Accepted" | "Draft" | "Declined";
const timeframeOptions: { key: TimeframeKey; label: string }[] = [
  { key: "last_week", label: "Last 1 week" },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "3_months", label: "Past 3 months" },
  { key: "6_months", label: "Past 6 months" },
  { key: "this_year", label: "This Year" },
  { key: "last_year", label: "Last Year" },
  { key: "overall", label: "Overall" },
];
// --- Timeframe Helper ---
const getTimeframeDate = (key: TimeframeKey): dayjs.Dayjs => {
  const now = dayjs();

  switch (key) {
    case "last_week":
      return now.subtract(7, "day").startOf("day");

    case "this_month":
      return now.startOf("month");

    case "last_month":
      return now.subtract(1, "month").startOf("month");

    case "3_months":
      return now.subtract(3, "month").startOf("day");

    case "6_months":
      return now.subtract(6, "month").startOf("day");

    case "this_year":
      return now.startOf("year");

    case "last_year":
      return now.subtract(1, "year").startOf("year");

    case "overall":
    default:
      return dayjs(0);
  }
};

// --- Global & Local Filter Resolver ---
const getStartDateFor = (
  key: TimeframeKey | null,
  globalStart: dayjs.Dayjs
): dayjs.Dayjs => {
  if (!key) return globalStart; // USE GLOBAL FILTER
  return getTimeframeDate(key); // USE OVERRIDE
};

export default function ProposalInsights() {
  const route = all_routes;
  const userId = useSelector((s: RootState) => s.auth.user_id);
  const [proposals, setProposals] = useState<ProposalResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [timeframe, setTimeframe] = useState<TimeframeKey>("this_month");
  const [statusFilter, setStatusFilter] = useState<string[] | null>(null); // null => all
  const [localStatusFilter, setLocalStatusFilter] =
    useState<TimeframeKey | null>(null);
  const [localOwnerFilter, setLocalOwnerFilter] = useState<TimeframeKey | null>(
    null
  );
  const [localProductFilter, setLocalProductFilter] =
    useState<TimeframeKey | null>(null);
  const [visibleStatus, setVisibleStatus] = useState<Record<string, boolean>>(
    {}
  );
  const [ownerLegend, setOwnerLegend] = useState<
    Record<OwnerStatusKey, boolean>
  >({
    Accepted: true,
    Draft: true,
    Declined: true,
  });
  const [productLegend, setProductLegend] = useState<
    Record<ProductStatusKey, boolean>
  >({
    Accepted: true,
    Draft: true,
    Declined: true,
  });

  // fetch
  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const data = await fetchProposalsByUserId(userId);
      setProposals(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load proposals for insights", err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    load();
  }, [load]);


  // timeframe helper -> start date
  const timeframeStart = useMemo(() => {
    const now = dayjs();
    switch (timeframe) {
      case "last_week":
        return now.subtract(7, "day").startOf("day");
      case "this_month":
        return now.startOf("month");
      case "last_month":
        return now.subtract(1, "month").startOf("month");
      case "3_months":
        return now.subtract(3, "month").startOf("day");
      case "6_months":
        return now.subtract(6, "month").startOf("day");
      case "overall":
      default:
        return dayjs(0);
    }
  }, [timeframe]);

  // filtered proposals by timeframe & optional status
  const filtered = useMemo(() => {
    const start = timeframeStart;
    return proposals.filter((p) => {
      const created = p.created_at ? dayjs(p.created_at) : null;
      const meetsDate = created
        ? created.isAfter(start) || created.isSame(start)
        : true;
      const meetsStatus =
        !statusFilter || statusFilter.length === 0
          ? true
          : statusFilter.includes(p.status || "");
      return meetsDate && meetsStatus;
    });
  }, [proposals, timeframeStart, statusFilter]);

  // KPI numbers
  const kpis = useMemo(() => {
    const total = filtered.length;
    let totalValue = 0;
    let acceptedValue = 0;
    filtered.forEach((p) => {
      const v = Number(p.proposal_total_amount || 0) || 0;
      totalValue += v;
      if ((p.status || "").toLowerCase() === "accepted") acceptedValue += v;
    });
    return {
      total,
      totalValue,
      acceptedValue,
      avgValue: total ? totalValue / total : 0,
    };
  }, [filtered]);

  // ------------------ Compute STATUS Distribution ------------------
  const computeStatusDist = (list: ProposalResponse[]) => {
    const map: Record<string, number> = {};

    list.forEach((p) => {
      const s = p.status || "Unknown";
      map[s] = (map[s] || 0) + 1;
    });

    return Object.entries(map).map(([name, value]) => ({
      name,
      value,
    }));
  };

  // ------------------ Compute LEAD OWNER Activity ------------------
  const computeLeadOwners = (list: ProposalResponse[]) => {
    const map = new Map<
      string,
      {
        owner: string;
        total: number;
        Draft: number;
        Accepted: number;
        Declined: number;
      }
    >();

    list.forEach((p) => {
      const owner = p.leads?.contact?.users?.name || "Unassigned";

      if (!map.has(owner)) {
        map.set(owner, { owner, total: 0, Draft: 0, Accepted: 0, Declined: 0 });
      }

      const rec = map.get(owner)!;
      rec.total += 1;

      const st = (p.status || "").toLowerCase();
      if (st === "draft") rec.Draft += 1;
      else if (st === "accepted") rec.Accepted += 1;
      else if (st === "declined") rec.Declined += 1;
    });

    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  };

  // ------------------ Compute PRODUCT Stats ------------------
  const computeProducts = (list: ProposalResponse[]) => {
    const map = new Map<
      string,
      {
        product: string;
        totalOrders: number;
        totalRevenue: number;
        Draft: number; // ← Capital D
        Accepted: number; // ← Capital A
        Declined: number; // ← Capital D
      }
    >();

    list.forEach((p) => {
      const prod = p.product_name || "Unspecified";
      const rev = Number(p.proposal_total_amount || 0) || 0;

      if (!map.has(prod)) {
        map.set(prod, {
          product: prod,
          totalOrders: 0,
          totalRevenue: 0,
          Draft: 0,
          Accepted: 0,
          Declined: 0,
        });
      }

      const rec = map.get(prod)!;
      rec.totalOrders++;
      rec.totalRevenue += rev;

      const st = (p.status || "").toLowerCase();
      if (st === "draft") rec.Draft++;
      else if (st === "accepted") rec.Accepted++;
      else if (st === "declined") rec.Declined++;
    });

    return Array.from(map.values()).sort(
      (a, b) => b.totalRevenue - a.totalRevenue
    );
  };

  // ------------------ STATUS BLOCK FILTERED DATA ------------------
  const filteredForStatus = useMemo(() => {
    const start = getStartDateFor(localStatusFilter, timeframeStart);

    return proposals.filter((p) => {
      const created = p.created_at ? dayjs(p.created_at) : null;
      return created ? created.isAfter(start) || created.isSame(start) : false;
    });
  }, [proposals, localStatusFilter, timeframeStart]);

  // ------------------ OWNER BLOCK FILTERED DATA ------------------
  const filteredForOwners = useMemo(() => {
    const start = getStartDateFor(localOwnerFilter, timeframeStart);

    return proposals.filter((p) => {
      const created = p.created_at ? dayjs(p.created_at) : null;
      return created ? created.isAfter(start) || created.isSame(start) : false;
    });
  }, [proposals, localOwnerFilter, timeframeStart]);

  // ------------------ PRODUCT BLOCK FILTERED DATA ------------------
  const filteredForProducts = useMemo(() => {
    const start = getStartDateFor(localProductFilter, timeframeStart);

    return proposals.filter((p) => {
      const created = p.created_at ? dayjs(p.created_at) : null;
      return created ? created.isAfter(start) || created.isSame(start) : false;
    });
  }, [proposals, localProductFilter, timeframeStart]);

  // FINAL computed values per block (with override-friendly filtering)
  const statusDistForBlock = useMemo(
    () => computeStatusDist(filteredForStatus),
    [filteredForStatus]
  );
  useEffect(() => {
    if (statusDistForBlock?.length) {
      const initial = statusDistForBlock.reduce((acc, item) => {
        acc[item.name] = true;
        return acc;
      }, {} as Record<string, boolean>);
      setVisibleStatus(initial);
    }
  }, [statusDistForBlock]);

  const LeadOwnerTooltip = ({
    active,
    payload,
    label,
  }: TooltipProps<any, any>) => {
    if (!active || !payload || payload.length === 0) return null;

    // Extract counts safely
    const accepted =
      payload.find((p: any) => p.dataKey === "Accepted")?.value ?? 0;
    const draft = payload.find((p: any) => p.dataKey === "Draft")?.value ?? 0;
    const declined =
      payload.find((p: any) => p.dataKey === "Declined")?.value ?? 0;

    const total = accepted + draft + declined;

    return (
      <div
        style={{
          background: "#fff",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #e6e6e6",
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          minWidth: 140,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>

        <div style={{ fontSize: 13 }}>
          <div style={{ color: STATUS_COLORS.Accepted }}>
            Accepted: <strong>{accepted}</strong>
          </div>
          <div style={{ color: STATUS_COLORS.Draft }}>
            Draft: <strong>{draft}</strong>
          </div>
          <div style={{ color: STATUS_COLORS.Declined }}>
            Declined: <strong>{declined}</strong>
          </div>

          <hr style={{ margin: "6px 0" }} />

          <div>
            Total: <strong>{total}</strong>
          </div>
        </div>
      </div>
    );
  };
  const ProductTooltip = ({
    active,
    payload,
    label,
  }: TooltipProps<any, any>) => {
    if (!active || !payload || payload.length === 0) return null;

    const accepted =
      payload.find((p: any) => p.dataKey === "Accepted")?.value ?? 0;
    const draft = payload.find((p: any) => p.dataKey === "Draft")?.value ?? 0;
    const declined =
      payload.find((p: any) => p.dataKey === "Declined")?.value ?? 0;
    const total = accepted + draft + declined;

    return (
      <div
        style={{
          background: "#fff",
          padding: "10px 12px",
          borderRadius: 10,
          border: "1px solid #e6e6e6",
          boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
          minWidth: 140,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>

        <div style={{ fontSize: 13 }}>
          <div style={{ color: STATUS_COLORS.Accepted }}>
            Accepted: <strong>{accepted}</strong>
          </div>
          <div style={{ color: STATUS_COLORS.Draft }}>
            Draft: <strong>{draft}</strong>
          </div>
          <div style={{ color: STATUS_COLORS.Declined }}>
            Declined: <strong>{declined}</strong>
          </div>

          <hr style={{ margin: "6px 0" }} />

          <div>
            Total: <strong>{total}</strong>
          </div>
        </div>
      </div>
    );
  };

  const leadOwnerDataForBlock = useMemo(
    () => computeLeadOwners(filteredForOwners),
    [filteredForOwners]
  );

  const productDataForBlock = useMemo(
    () => computeProducts(filteredForProducts),
    [filteredForProducts]
  );

  // upcoming validation dates (next 30 days)
  const upcomingValidations = useMemo(() => {
    const now = dayjs();
    return proposals
      .filter((p) => p.validation_date)
      .map((p) => ({ ...p, validationDateObj: dayjs(p.validation_date) }))
      .filter(
        (p: any) =>
          (p.validationDateObj.isAfter(now) ||
            p.validationDateObj.isSame(now)) &&
          p.validationDateObj.isBefore(now.add(30, "day"))
      )
      .sort(
        (a: any, b: any) =>
          a.validationDateObj.valueOf() - b.validationDateObj.valueOf()
      );
  }, [proposals]);

  // upcoming proposals by proposal date (next 30 days)
  const upcomingProposals = useMemo(() => {
    const now = dayjs();
    return proposals
      .filter((p) => p.Date_of_proposal)
      .map((p) => ({ ...p, proposalDateObj: dayjs(p.Date_of_proposal) }))
      .filter(
        (p: any) =>
          (p.proposalDateObj.isAfter(now) || p.proposalDateObj.isSame(now)) &&
          p.proposalDateObj.isBefore(now.add(30, "day"))
      )
      .sort(
        (a: any, b: any) =>
          a.proposalDateObj.valueOf() - b.proposalDateObj.valueOf()
      );
  }, [proposals]);

  // small helper to format currency (naive)
  const fmtCurrency = (v: number) =>
    v.toLocaleString(undefined, { maximumFractionDigits: 0 });

  return (
    <>
      <PageLoadProgressBar />
      <div className="page-wrapper">
        <Container fluid className="py-3 content container-fluid">
          <Row className="mb-3 align-items-center">
            <Col md="6">
              <h3 className="mb-0 d-flex align-items-center gap-2">
                <i className="ti ti-graph text-primary"></i>
                Proposal Insights
              <Tip text="Complete analytics for proposals, including values, revenue, owners and product performance." placement="right">
  <i
    className="fa fa-info-circle text-primary"
    style={{ cursor: "pointer", fontSize: "16px" }}
  />
</Tip>

              </h3>

              <small className="text-muted small">
                Overview of proposals and revenue
              </small>
               {/* Breadcrumb */}
                                <ol className="breadcrumb mb-0 small">
                                  <li className="breadcrumb-item">
                                    <Link to={route.ProposalsList} 
                                     style={{
                                      color: "#9595b5",
                                      textDecoration: "none",
                                      transition: "color 0.2s ease",
                                    }}
                                    onMouseEnter={(e) =>
                                      (e.currentTarget.style.color = "#E41F07")
                                    }
                                    onMouseLeave={(e) =>
                                      (e.currentTarget.style.color = "#9595b5")
                                    }
                                    >
                                      Quotes
                                    </Link>
                                  </li>
                                  <li className="breadcrumb-item active">Proposal Insights</li>
                                </ol>
                             
            </Col>
            <Col md="6" className="text-end">
              <div className="d-inline-flex align-items-center gap-2">
                <Form.Select
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value as TimeframeKey)}
                  style={{ width: 180 }}
                >
                  {timeframeOptions.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}
                    </option>
                  ))}
                </Form.Select>

                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-secondary"
                    id="status-filter"
                  >
                    {statusFilter && statusFilter.length
                      ? statusFilter.join(", ")
                      : "All Status"}
                  </Dropdown.Toggle>
                  <Dropdown.Menu align="end" style={{ minWidth: 200 }}>
                    <div className="px-3 py-2">
                      {["Draft", "Accepted", "Declined"].map((s) => {
                        const checked = statusFilter?.includes(s) ?? false;
                        return (
                          <Form.Check
                            key={s}
                            type="checkbox"
                            id={`status-${s}`}
                            label={`${s}`}
                            checked={checked}
                            onChange={(e) => {
                              let next = statusFilter ? [...statusFilter] : [];
                              if (e.target.checked) next.push(s);
                              else next = next.filter((x) => x !== s);
                              setStatusFilter(next.length ? next : null);
                            }}
                            className="mb-1"
                          />
                        );
                      })}
                      <div className="d-flex justify-content-between mt-2">
                        <Button
                          size="sm"
                          variant="light"
                          onClick={() => setStatusFilter(null)}
                        >
                          Clear
                        </Button>
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => {
                            /* no-op */
                          }}
                        >
                          Apply
                        </Button>
                      </div>
                    </div>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Col>
          </Row>

          {/* KPI cards */}
          <Row className="mb-3 g-3">
            {/* Total Proposals */}
            <Col md={3} className="d-flex">
              <Card className="shadow-sm border-0 dashboard-card h-100 w-100">
                <Card.Body className="py-3 d-flex align-items-center">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                      <h6 className="text-muted mb-1">Total Proposals</h6>
                      <h3 className="fw-bold mb-0">{kpis.total}</h3>
                      <small className="text-success">
                        In selected timeframe
                      </small>
                    </div>
                    <div className="kpi-icon bg-primary bg-opacity-10 text-primary">
                      <i className="bi bi-stack fs-3"></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Total Value */}
            <Col md={3} className="d-flex">
              <Card className="shadow-sm border-0 dashboard-card h-100 w-100">
                <Card.Body className="py-3 d-flex align-items-center">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                      <h6 className="text-muted mb-1">Total Value</h6>
                      <h3 className="fw-bold mb-0">
                        ₹ {fmtCurrency(kpis.totalValue)}
                      </h3>
                      <small className="text-primary">
                        Overall proposal amount
                      </small>
                    </div>
                    <div className="kpi-icon bg-info bg-opacity-10 text-info">
                      <i className="bi bi-currency-rupee fs-3"></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Accepted Revenue */}
            <Col md={3} className="d-flex">
              <Card className="shadow-sm border-0 dashboard-card h-100 w-100">
                <Card.Body className="py-3 d-flex align-items-center">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                      <h6 className="text-muted mb-1">Accepted Revenue</h6>
                      <h3 className="fw-bold mb-0">
                        ₹ {fmtCurrency(kpis.acceptedValue)}
                      </h3>
                      <small className="text-success">
                        From accepted proposals
                      </small>
                    </div>
                    <div className="kpi-icon bg-success bg-opacity-10 text-success">
                      <i className="bi bi-check2-circle text-success fs-3"></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Average Proposal Value */}
            <Col md={3} className="d-flex">
              <Card className="shadow-sm border-0 dashboard-card h-100 w-100">
                <Card.Body className="py-3 d-flex align-items-center">
                  <div className="d-flex justify-content-between align-items-center w-100">
                    <div>
                      <h6 className="text-muted mb-1">Avg Proposal Value</h6>
                      <h3 className="fw-bold mb-0">
                        ₹ {fmtCurrency(kpis.avgValue)}
                      </h3>
                      <small className="text-warning">Average deal size</small>
                    </div>
                    <div className="kpi-icon bg-warning bg-opacity-10 text-warning">
                      <i className="bi bi-graph-up-arrow text-warning fs-3"></i>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="gy-3">
            {/* Left column with charts */}
            <Col lg={12}>
              <Row className="gy-4">
                {/* ------------------------- Status Distribution ------------------------- */}
                {/* ------------------------- Status Distribution ------------------------- */}
                <Col md={6}>
                  <Card className="shadow-sm border-0 rounded-4 analytics-card">
                    <Card.Body>
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-pie-chart-fill text-primary"></i>
                            <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                              Quotes Status Distribution
                           <Tip text="Breakdown of proposals by Draft, Accepted, and Declined status.">
  <i
    className="fa fa-info-circle text-primary me-2"
    style={{ cursor: "pointer", fontSize: "14px" }}
  />
</Tip>

                            </h5>
                          </div>
                          <small className="text-muted">
                            Proposal breakdown by status
                          </small>
                        </div>

                        <Form.Select
                          size="sm"
                          className="w-auto "
                          value={localStatusFilter || ""}
                          onChange={(e) =>
                            setLocalStatusFilter(
                              e.target.value === ""
                                ? null
                                : (e.target.value as TimeframeKey)
                            )
                          }
                        >
                          <option value="">Global</option>
                          {timeframeOptions.map((t) => (
                            <option key={t.key} value={t.key}>
                              {t.label}
                            </option>
                          ))}
                        </Form.Select>
                      </div>

                      {/* PIE CHART */}
                      {proposals.length === 0 || statusDistForBlock.every(s => s.value === 0) ? (
                        <div className="text-center py-5">
                          <i className="bi bi-file-earmark-text text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                          <p className="text-muted mb-0 fw-semibold">
                            No data found for quotes insights
                          </p>
                        </div>
                      ) : (
                        <div style={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={statusDistForBlock.filter(
                                  (d) => visibleStatus[d.name]
                                )}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                innerRadius={55}
                                outerRadius={90}
                                paddingAngle={3}
                                labelLine={false}
                                animationBegin={0}
                                animationDuration={900}
                                label={({ name, value }) => `${name} (${value})`}
                              >
                                {statusDistForBlock
                                  .filter((d) => visibleStatus[d.name])
                                  .map((entry, i) => (
                                    <Cell
                                      key={i}
                                      fill={
                                        STATUS_COLORS[entry.name] ||
                                        COLORS[i % COLORS.length]
                                      }
                                      stroke="#fff"
                                      strokeWidth={2}
                                    />
                                  ))}
                              </Pie>

                              <ReTooltip
                                contentStyle={{
                                  borderRadius: 12,
                                  border: "1px solid #e8e8e8",
                                  padding: 10,
                                  boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* PROFESSIONAL CLICKABLE LEGEND */}
                      {proposals.length > 0 && !statusDistForBlock.every(s => s.value === 0) && (
                        <div className="d-flex flex-wrap gap-2 mt-3">
                          {statusDistForBlock.map((s, i) => {
                          const color =
                            STATUS_COLORS[s.name] || COLORS[i % COLORS.length];

                          const active = visibleStatus[s.name];

                          return (
                            <span
                              key={i}
                              onClick={() =>
                                setVisibleStatus((prev) => ({
                                  ...prev,
                                  [s.name]: !prev[s.name], // toggle visibility
                                }))
                              }
                              className={`badge px-3 py-2 rounded-pill legend-pill ${
                                active ? "legend-active" : "legend-inactive"
                              }`}
                              style={{
                                border: `1px solid ${color}`,
                                background: active ? color + "22" : "#f0f0f0",
                                cursor: "pointer",
                                userSelect: "none",
                                fontSize: "0.8rem",
                                transition: "0.25s ease",
                              }}
                            >
                              <span
                                className="legend-dot"
                                style={{
                                  display: "inline-block",
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  background: color,
                                  marginRight: 6,
                                  opacity: active ? 1 : 0.4,
                                }}
                              ></span>
                              {s.name} — <strong>{s.value}</strong>
                            </span>
                          );
                        })}
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                {/* ------------------------- Lead Owner Activity ------------------------- */}
                <Col md={6}>
                  <Card className="shadow-sm border-0 rounded-4">
                    <Card.Body>
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2">
                          <i className="bi bi-people-fill text-success"></i>
                          <h4 className="mb-0 d-flex align-items-center gap-2">
                            Top Lead Owners
                           <Tip text="Shows performance of lead owners based on their proposals and status counts.">
  <i
    className="fa fa-info-circle text-primary me-2"
    style={{ cursor: "pointer", fontSize: "14px" }}
  />
</Tip>

                          </h4>
                        </div>

                        {/* Local Filter */}
                        <Form.Select
                          size="sm"
                          className="w-auto"
                          value={localOwnerFilter || ""}
                          onChange={(e) =>
                            setLocalOwnerFilter(
                              e.target.value === ""
                                ? null
                                : (e.target.value as TimeframeKey)
                            )
                          }
                        >
                          <option value="">Global </option>
                          {timeframeOptions.map((t) => (
                            <option key={t.key} value={t.key}>
                              {t.label}
                            </option>
                          ))}
                        </Form.Select>
                      </div>

                      {proposals.length === 0 || leadOwnerDataForBlock.length === 0 ? (
                        <div className="text-center py-5">
                          <i className="bi bi-file-earmark-text text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                          <p className="text-muted mb-0 fw-semibold">
                            No data found for quotes insights
                          </p>
                        </div>
                      ) : (
                        <div style={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={leadOwnerDataForBlock}>
                              <XAxis dataKey="owner" hide />
                              <YAxis />

                              <ReTooltip content={<LeadOwnerTooltip />} />

                              <Legend
                                onClick={(e) => {
                                  if (!e || !e.dataKey) return;
                                  const key = e.dataKey as OwnerStatusKey;
                                  setOwnerLegend((prev) => ({
                                    ...prev,
                                    [key]: !prev[key],
                                  }));
                                }}
                                wrapperStyle={{ cursor: "pointer" }}
                              />

                              {ownerLegend.Accepted && (
                                <Bar
                                  dataKey="Accepted"
                                  stackId="a"
                                  fill={STATUS_COLORS.Accepted}
                                />
                              )}
                              {ownerLegend.Draft && (
                                <Bar
                                  dataKey="Draft"
                                  stackId="a"
                                  fill={STATUS_COLORS.Draft}
                                />
                              )}
                              {ownerLegend.Declined && (
                                <Bar
                                  dataKey="Declined"
                                  stackId="a"
                                  fill={STATUS_COLORS.Declined}
                                />
                              )}
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      )}

                      {/* <-- MOVE SCROLL CONTAINER OUTSIDE */}
                      <div
                        className="mt-2 small text-muted"
                        style={{
                          maxHeight: "120px",
                          overflowY: "auto",
                          paddingRight: "6px",
                        }}
                      >
                        {leadOwnerDataForBlock.map((l) => (
                          <div key={String(l.owner)}>
                            {l.owner} — {l.total} proposals
                          </div>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={12}>
                  <Card className="shadow-sm border-0 rounded-4 analytics-card">
                    <Card.Body>
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-box-seam text-warning"></i>
                            <h5 className="mb-0 fw-semibold d-flex align-items-center gap-2">
                              Product-wise Revenue & Status
                            <Tip text="Revenue generated per product along with status-wise breakdown.">
  <i
    className="fa fa-info-circle text-primary me-2"
    style={{ cursor: "pointer", fontSize: "14px" }}
  />
</Tip>

                            </h5>
                          </div>

                          <small className="text-muted">
                            Performance of products based on proposal activity
                          </small>
                        </div>

                        <Form.Select
                          size="sm"
                          className="w-auto insight-filter-modern"
                          value={localProductFilter || ""}
                          onChange={(e) =>
                            setLocalProductFilter(
                              e.target.value === ""
                                ? null
                                : (e.target.value as TimeframeKey)
                            )
                          }
                        >
                          <option value="">Global</option>
                          {timeframeOptions.map((t) => (
                            <option key={t.key} value={t.key}>
                              {t.label}
                            </option>
                          ))}
                        </Form.Select>
                      </div>

                      {/* Chart */}
                      {proposals.length === 0 || productDataForBlock.length === 0 ? (
                        <div className="text-center py-5">
                          <i className="bi bi-file-earmark-text text-muted mb-3" style={{ fontSize: "48px", opacity: 0.5 }}></i>
                          <p className="text-muted mb-0 fw-semibold">
                            No data found for quotes insights
                          </p>
                        </div>
                      ) : (
                        <>
                          <div style={{ height: 320 }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart data={productDataForBlock}>
                                <XAxis dataKey="product" tick={{ fontSize: 11 }} />
                                <YAxis />

                                <ReTooltip content={<ProductTooltip />} />

                                {/* Interactive Legend */}
                                <Legend
                                  onClick={(e) => {
                                    if (!e || !e.dataKey) return;
                                    const key = e.dataKey as ProductStatusKey;
                                    setProductLegend((prev) => ({
                                      ...prev,
                                      [key]: !prev[key],
                                    }));
                                  }}
                                  wrapperStyle={{ cursor: "pointer" }}
                                />

                                {productLegend.Accepted && (
                                  <Bar
                                    dataKey="Accepted"
                                    name="Accepted"
                                    stackId="a"
                                    fill={STATUS_COLORS.Accepted}
                                    radius={[6, 6, 0, 0]}
                                  />
                                )}

                                {productLegend.Draft && (
                                  <Bar
                                    dataKey="Draft"
                                    name="Draft"
                                    stackId="a"
                                    fill={STATUS_COLORS.Draft}
                                    radius={[6, 6, 0, 0]}
                                  />
                                )}

                                {productLegend.Declined && (
                                  <Bar
                                    dataKey="Declined"
                                    name="Declined"
                                    stackId="a"
                                    fill={STATUS_COLORS.Declined}
                                    radius={[6, 6, 0, 0]}
                                  />
                                )}
                              </BarChart>
                            </ResponsiveContainer>
                          </div>

                          {/* Top Products Section */}
                          <div className="mt-4">
                            <h6 className="fw-semibold mb-3">Top Products</h6>

                            <div className="d-flex gap-3 flex-wrap">
                              {productDataForBlock.map((p) => (
                            <div
                              key={p.product}
                              className="p-3 rounded product-tile shadow-sm"
                              style={{
                                borderLeft: `4px solid ${STATUS_COLORS.Accepted}`,
                                background: "#fafbff",
                              }}
                            >
                              <div className="fw-bold">{p.product}</div>
                              <div className="small text-muted">
                                Orders: {p.totalOrders}
                              </div>
                              <div className="small fw-semibold">
                                Revenue: ₹ {fmtCurrency(p.totalRevenue)}
                              </div>
                            </div>
                          ))}
                            </div>
                          </div>
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>

            {/* Right column with lists */}
            <Col lg={6}>
              {/* Upcoming Validation Dates */}
              <Card className="mb-3 shadow-sm border-0 rounded-4">
                <Card.Body>
                  <h6 className="mb-3 fw-semibold d-flex align-items-center gap-2">
                    <i className="bi bi-calendar-check text-primary"></i>
                    Upcoming Validation Dates (next 30d)
                 <Tip text="These proposals have validation dates approaching within the next 30 days.">
  <i
    className="fa fa-info-circle text-primary"
    style={{ cursor: "pointer", fontSize: "14px" }}
  />
</Tip>

                  </h6>

                  <div
                    style={{
                      maxHeight: "260px",
                      overflowY: "auto",
                      paddingRight: 6,
                    }}
                  >
                    {upcomingValidations.length ? (
                      upcomingValidations.map((p) => (
                        <div
                          key={p.proposal_id}
                          className="d-flex justify-content-between align-items-center py-2 px-1 item-row"
                        >
                          <div>
                            <div className="fw-semibold text-dark">
                              {p.leads?.contact?.company_name || p.contact_name}
                            </div>

                            <small className="text-muted d-block">
                              {p.product_name} • {p.leads?.contact?.users?.name|| "-"}
                            </small>
                          </div>

                          <div className="text-end">
                            <span className="badge bg-light text-dark border">
                              {dayjs(p.validation_date).format("DD MMM")}
                            </span>

                            <div className="small text-muted mt-1">
                              in {dayjs(p.validation_date).diff(dayjs(), "day")}{" "}
                              d
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-state text-muted text-center py-3">
                        <i className="bi bi-calendar-x fs-4 d-block mb-2"></i>
                        No upcoming validations
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={6}>
              {/* Upcoming Proposal Dates */}
              <Card className="mb-3 shadow-sm border-0 rounded-4">
                <Card.Body>
                  <h6 className="mb-3 fw-semibold d-flex align-items-center gap-2">
                    <i className="bi bi-calendar-event text-info"></i>
                    Upcoming Proposal Dates (next 30d)
                   <Tip text="Proposals scheduled to be generated or sent within the upcoming 30 days.">
  <i
    className="fa fa-info-circle text-primary"
    style={{ cursor: "pointer", fontSize: "14px" }}
  />
</Tip>

                  </h6>

                  <div
                    style={{
                      maxHeight: "260px",
                      overflowY: "auto",
                      paddingRight: 6,
                    }}
                  >
                    {upcomingProposals.length ? (
                      upcomingProposals.map((p) => (
                        <div
                          key={p.proposal_id}
                          className="d-flex justify-content-between align-items-center py-2 px-1 item-row"
                        >
                          <div>
                            <div className="fw-semibold text-dark">
                              {p.leads?.contact?.company_name || p.contact_name}
                            </div>
                            <small className="text-muted">
                              {p.product_name}
                            </small>
                          </div>

                          <div className="text-end">
                            <span className="badge bg-light text-dark border">
                              {dayjs(p.Date_of_proposal).format("DD MMM")}
                            </span>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="empty-state text-muted text-center py-3">
                        <i className="bi bi-calendar-x fs-4 d-block mb-2"></i>
                        No upcoming proposals
                      </div>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ScrollToTop />
    </>
  );
}
	